import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useAppData } from './AppDataContext';
import { newsService } from '../services/newsService';

const NewsFeedContext = createContext();

export const useNewsFeed = () => {
  const context = useContext(NewsFeedContext);
  if (!context) {
    throw new Error('useNewsFeed must be used within a NewsFeedProvider');
  }
  return context;
};

const DEFAULT_FILTERS = {
  q: '',
  topic: 'all',
  region: 'all',
  severity: 'all',
  ticker: '',
  industry: '',
  dateFrom: '',
  dateTo: '',
};

const DEFAULT_QUICK_COUNTS = {
  allNews: 0,
  positiveCount: 0,
  negativeCount: 0,
  savedArticles: 0,
  watchlistNews: 0,
};

const DEFAULT_FILTER_OPTIONS = {
  topics: [],
  regions: [],
  severityLevels: [],
};

export const NewsFeedProvider = ({ children }) => {
  const { user } = useAuth();
  const { companies, watchlist, dashboardBootstrap } = useAppData();
  const userId = user?.uid ?? null;

  const [feedArticles, setFeedArticles] = useState([]);
  const [feedPagination, setFeedPagination] = useState(null);
  const [savedArticles, setSavedArticles] = useState([]);
  const [filterOptions, setFilterOptions] = useState(DEFAULT_FILTER_OPTIONS);
  const [quickFilterCounts, setQuickFilterCounts] = useState(DEFAULT_QUICK_COUNTS);
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [savedArticleIds, setSavedArticleIds] = useState(new Set());
  const [filters, setFiltersState] = useState(DEFAULT_FILTERS);
  const [activeQuickFilter, setActiveQuickFilterState] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [error, setError] = useState(null);

  const watchlistTickers = React.useMemo(() => {
    const list = Array.isArray(watchlist) ? watchlist : [];
    return new Set(
      list
        .map((item) => item.ticker || item.companyId || item.company_id)
        .filter(Boolean)
        .map((s) => String(s).trim().toUpperCase())
    );
  }, [watchlist]);

  const setFilters = useCallback((next) => {
    setFiltersState((prev) => (typeof next === 'function' ? next(prev) : { ...prev, ...next }));
    setCurrentPage(1);
  }, []);

  const setActiveQuickFilter = useCallback((value) => {
    setActiveQuickFilterState(value);
    setCurrentPage(1);
  }, []);

  const loadFilters = useCallback(async () => {
    setLoadingMeta(true);
    try {
      const res = await newsService.getFilters();
      if (res.success && res.data) {
        setFilterOptions({
          topics: res.data.topics ?? [],
          regions: res.data.regions ?? [],
          severityLevels: res.data.severityLevels ?? [],
        });
      }
    } catch (err) {
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  const loadQuickCounts = useCallback(async () => {
    try {
      const baseFilters = {
        ticker: filters.ticker || undefined,
        industry: filters.industry || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
      };
      const res = await newsService.getQuickFilterCounts(userId, baseFilters);
      if (res.success && res.data) {
        setQuickFilterCounts({
          allNews: res.data.allNews ?? 0,
          positiveCount: res.data.positiveCount ?? 0,
          negativeCount: res.data.negativeCount ?? 0,
          savedArticles: res.data.savedArticles ?? 0,
          watchlistNews: res.data.watchlistNews ?? 0,
        });
      }
    } catch (err) {
    }
  }, [userId, filters.ticker, filters.industry, filters.dateFrom, filters.dateTo]);

  const loadTrending = useCallback(async () => {
    try {
      const res = await newsService.getTrendingTopics(10, 7);
      if (res.success && Array.isArray(res.data)) {
        setTrendingTopics(res.data);
      }
    } catch (err) {
    }
  }, []);

  const loadFeed = useCallback(async () => {
    setLoadingFeed(true);
    setError(null);
    try {
      let severity = filters.severity;
      if (activeQuickFilter === 'positive' || activeQuickFilter === 'negative') severity = undefined;
      else if (activeQuickFilter === 'all') severity = severity === 'all' ? undefined : severity;
      if (severity === 'all') severity = undefined;

      const res = await newsService.getNewsArticles({
        q: filters.q || undefined,
        topic: filters.topic !== 'all' ? filters.topic : undefined,
        region: filters.region !== 'all' ? filters.region : undefined,
        severity,
        ticker: filters.ticker || undefined,
        industry: filters.industry || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        page: currentPage,
        size: pageSize,
      });
      if (res.success) {
        const list = Array.isArray(res.data) ? res.data : [];
        setFeedArticles(list);
        setFeedPagination(res.pagination ?? null);
        setQuickFilterCounts((prev) => ({
          ...prev,
          positiveCount: list.filter((a) => (a.sentiment || 'neutral').toLowerCase() === 'positive').length,
          negativeCount: list.filter((a) => (a.sentiment || 'neutral').toLowerCase() === 'negative').length,
        }));
      } else {
        setError(res.message || 'Failed to load feed');
        setFeedArticles([]);
        setFeedPagination(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load feed');
      setFeedArticles([]);
      setFeedPagination(null);
    } finally {
      setLoadingFeed(false);
    }
  }, [
    filters.q,
    filters.topic,
    filters.region,
    filters.severity,
    filters.ticker,
    filters.industry,
    filters.dateFrom,
    filters.dateTo,
    activeQuickFilter,
    currentPage,
    pageSize,
  ]);

  const loadSaved = useCallback(async () => {
    if (!userId) {
      setSavedArticles([]);
      setSavedArticleIds(new Set());
      return;
    }
    setLoadingSaved(true);
    try {
      const res = await newsService.getSavedArticles(userId);
      if (res.success && Array.isArray(res.data)) {
        setSavedArticles(res.data);
        const ids = res.data
          .map((a) => String(a?.articleId ?? a?.article_id ?? a?.id ?? '').trim())
          .filter(Boolean);
        setSavedArticleIds(new Set(ids));
      } else {
        setSavedArticles([]);
        setSavedArticleIds(new Set());
      }
    } catch (err) {
      setSavedArticles([]);
      setSavedArticleIds(new Set());
    } finally {
      setLoadingSaved(false);
    }
  }, [userId]);

  useEffect(() => {
    const sidebar = dashboardBootstrap?.newsSidebar;
    if (sidebar?.quick_filter_counts || sidebar?.quickFilterCounts) {
      const counts = sidebar.quick_filter_counts ?? sidebar.quickFilterCounts;
      setQuickFilterCounts({
        allNews: counts.allNews ?? counts.all_news ?? 0,
        positiveCount: counts.positiveCount ?? counts.positive_count ?? 0,
        negativeCount: counts.negativeCount ?? counts.negative_count ?? 0,
        savedArticles: counts.savedArticles ?? counts.saved_articles ?? 0,
        watchlistNews: counts.watchlistNews ?? counts.watchlist_news ?? 0,
      });
    }
    const topics = sidebar?.trending_topics ?? sidebar?.trendingTopics;
    if (Array.isArray(topics) && topics.length > 0) {
      setTrendingTopics(topics);
    }
  }, [dashboardBootstrap]);

  useEffect(() => {
    loadFilters();
    if (!dashboardBootstrap?.newsSidebar) {
      loadTrending();
    }
  }, [loadFilters, loadTrending, dashboardBootstrap]);

  useEffect(() => {
    loadQuickCounts();
  }, [loadQuickCounts]);

  useEffect(() => {
    if (!userId) {
      setSavedArticleIds(new Set());
      setSavedArticles([]);
      return;
    }
    loadSaved();
  }, [userId, loadSaved]);

  useEffect(() => {
    if (activeQuickFilter === 'saved') {
      loadSaved();
      return;
    }
    loadFeed();
  }, [activeQuickFilter, loadFeed, loadSaved]);

  const articles = activeQuickFilter === 'saved' ? savedArticles : feedArticles;
  const pagination = activeQuickFilter === 'saved' ? null : feedPagination;
  const loading = activeQuickFilter === 'saved' ? loadingSaved : loadingFeed;

  const articlesForDisplay = React.useMemo(() => {
    if (activeQuickFilter !== 'watchlist' || !watchlistTickers.size) {
      return articles;
    }
    return articles.filter((a) => {
      const t = (a.ticker || '').toString().trim().toUpperCase();
      return t && watchlistTickers.has(t);
    });
  }, [activeQuickFilter, articles, watchlistTickers]);

  const saveArticle = useCallback(
    async (articleId) => {
      if (!userId) return { success: false };
      const res = await newsService.saveArticle(articleId, userId);
      if (res.success) {
        setSavedArticleIds((prev) => new Set([...prev, articleId]));
        await loadQuickCounts();
        if (activeQuickFilter === 'saved') await loadSaved();
      }
      return res;
    },
    [userId, loadQuickCounts, loadSaved, activeQuickFilter]
  );

  const unsaveArticle = useCallback(
    async (articleId) => {
      if (!userId) return { success: false };
      const res = await newsService.unsaveArticle(articleId, userId);
      if (res.success) {
        setSavedArticleIds((prev) => {
          const next = new Set(prev);
          next.delete(articleId);
          return next;
        });
        await loadQuickCounts();
        if (activeQuickFilter === 'saved') await loadSaved();
      }
      return res;
    },
    [userId, loadQuickCounts, loadSaved, activeQuickFilter]
  );

  const clearFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
    setActiveQuickFilterState('all');
    setCurrentPage(1);
  }, []);

  const value = {
    articles: articlesForDisplay,
    pagination,
    loading,
    error,
    filterOptions,
    quickFilterCounts,
    trendingTopics,
    savedArticleIds,
    filters,
    setFilters,
    activeQuickFilter,
    setActiveQuickFilter,
    currentPage,
    setCurrentPage,
    pageSize,
    companies,
    loadFeed,
    loadQuickCounts,
    loadSaved,
    saveArticle,
    unsaveArticle,
    clearFilters,
    userId,
  };

  return (
    <NewsFeedContext.Provider value={value}>
      {children}
    </NewsFeedContext.Provider>
  );
};
